import { useEffect, useRef } from 'react';
import { useSocket } from './SocketContext';

/**
 * Souscrit à un évènement socket et le nettoie automatiquement.
 *
 * Le handler est encapsulé dans un wrapper stable via `useRef`, pour ne pas
 * re-souscrire à chaque rendu (et garantir un `off` avec la même référence
 * que le `on`). Plusieurs composants peuvent ainsi écouter le même évènement
 * sans s'effacer mutuellement comme avec `socket.off('event')`.
 */
export const useSocketEvent = <T extends any[] = any[]>(
  event: string,
  handler: (...args: T) => void,
  enabled: boolean = true,
): void => {
  const { socket } = useSocket();
  const handlerRef = useRef(handler);

  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    if (!socket || !enabled) return;

    const wrapper = (...args: T) => handlerRef.current(...args);
    socket.on(event, wrapper as (...args: unknown[]) => void);

    return () => {
      socket.off(event, wrapper as (...args: unknown[]) => void);
    };
  }, [socket, event, enabled]);
};
